import React, { useState, useEffect, useRef } from 'react';
import ContentEditable from 'react-contenteditable';
import useGenerateText from '../../hooks/useGenerateText';
import { stripAllFormatting } from '../../utils/textUtils';

interface QuestionVariations {
  original: string;
  tuSingular: string;
  tuPlural: string;
  udSingular: string;
  udPlural: string;
}

interface QuestionImageContent {
  text: string | QuestionVariations;
  imageUrl: string;
  imageFilename: string;
}

interface QuestionImageEditorProps {
  content: QuestionImageContent;
  onChange: (content: { text: QuestionVariations; imageUrl: string; imageFilename: string }) => void;
  uploadedImageUrl: string;
  onImageUrlChange: (url: string) => void;
  uploadedImageFilename: string;
  onImageFilenameChange: (filename: string) => void;
  isUploadingImage: boolean;
  onImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onImageDelete: () => void;
  isEditable: boolean;
  onEditableChange: (editable: boolean) => void;
  showEditableToggle?: boolean;
}

type TabKey = keyof QuestionVariations;

const QuestionImageEditor: React.FC<QuestionImageEditorProps> = ({
  content,
  onChange,
  uploadedImageUrl,
  onImageUrlChange,
  uploadedImageFilename,
  onImageFilenameChange,
  isUploadingImage,
  onImageUpload,
  onImageDelete,
  isEditable,
  onEditableChange,
  showEditableToggle = true
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>('original');
  const { generateText, isGenerating, error, clearError } = useGenerateText();
  
  // Utility function to fill empty variations with original content
  const fillEmptyVariationsWithOriginal = (variations: QuestionVariations): QuestionVariations => {
    const { original, tuSingular, tuPlural, udSingular, udPlural } = variations;
    
    // Check if variations are empty (all blank or just whitespace)
    const isEmpty = (text: string) => !text || text.trim() === '';
    const allVariationsEmpty = isEmpty(tuSingular) && isEmpty(tuPlural) && 
                               isEmpty(udSingular) && isEmpty(udPlural);
    
    // If original has content but variations are empty, fill them
    if (!isEmpty(original) && allVariationsEmpty) {
      return {
        ...variations,
        tuSingular: original,
        tuPlural: original,
        udSingular: original,
        udPlural: original
      };
    }
    
    return variations;
  };
  
  // Initialize variations from content prop
  const [variations, setVariations] = useState<QuestionVariations>(() => {
    let initialVariations: QuestionVariations;
    
    if (typeof content.text === 'string') {
      // Legacy format - use string as original
      initialVariations = {
        original: content.text,
        tuSingular: '',
        tuPlural: '',
        udSingular: '',
        udPlural: ''
      };
    } else if (content.text && typeof content.text === 'object') {
      // New format with variations
      initialVariations = {
        original: content.text.original || '',
        tuSingular: content.text.tuSingular || '',
        tuPlural: content.text.tuPlural || '',
        udSingular: content.text.udSingular || '',
        udPlural: content.text.udPlural || ''
      };
    } else {
      // Default empty state
      initialVariations = {
        original: '',
        tuSingular: '',
        tuPlural: '',
        udSingular: '',
        udPlural: ''
      };
    }
    
    // Auto-fill empty variations with original content if applicable
    return fillEmptyVariationsWithOriginal(initialVariations);
  });

  // Update parent whenever content changes (only call onChange when content actually changes)
  const prevContentRef = useRef<{text: QuestionVariations, imageUrl: string, imageFilename: string} | null>(null);

  useEffect(() => {
    const currentContent = {
      text: variations,
      imageUrl: uploadedImageUrl,
      imageFilename: uploadedImageFilename
    };
    
    // Only call onChange if content actually changed
    if (!prevContentRef.current ||
        JSON.stringify(prevContentRef.current.text) !== JSON.stringify(variations) ||
        prevContentRef.current.imageUrl !== uploadedImageUrl ||
        prevContentRef.current.imageFilename !== uploadedImageFilename) {
      onChange(currentContent);
      prevContentRef.current = { ...currentContent };
    }
  }, [variations, uploadedImageUrl, uploadedImageFilename]);

  const tabs = [
    { key: 'original' as TabKey, label: 'Original' },
    { key: 'tuSingular' as TabKey, label: 'TÚ SINGULAR' },
    { key: 'tuPlural' as TabKey, label: 'TÚ PLURAL' },
    { key: 'udSingular' as TabKey, label: 'UD. SINGULAR' },
    { key: 'udPlural' as TabKey, label: 'UD. PLURAL' }
  ];

  const applyFormatting = (command: string, value?: string) => {
    document.execCommand(command, false, value);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const handleContentChange = (value: string) => {
    setVariations(prev => {
      const updated = {
        ...prev,
        [activeTab]: value
      };
      
      // If we're editing the original tab and other tabs are empty, auto-fill them
      if (activeTab === 'original') {
        return fillEmptyVariationsWithOriginal(updated);
      }
      
      return updated;
    });
  };

  const handleAutoGenerate = async () => {
    // Check if we have original text to generate from
    if (!variations.original || variations.original.trim() === '') {
      alert('Por favor, ingresa primero el texto original antes de autogenerar las variaciones.');
      return;
    }

    // Only generate for non-original tabs
    if (activeTab === 'original') {
      return;
    }

    clearError();
    
    try {
      // Map activeTab to the correct type for the hook
      const variationType = activeTab as 'tuSingular' | 'tuPlural' | 'udSingular' | 'udPlural';
      
      const generatedText = await generateText(variations.original, variationType);
      
      // Update the specific variation with the generated text
      setVariations(prev => ({
        ...prev,
        [activeTab]: generatedText
      }));
      
    } catch (error) {
      // Error is already handled by the hook
      console.error('Error generating text:', error);
    }
  };

  return (
    <div className="mt-4 pt-2">
      {/* Image Section */}
      <div className="mb-4">
        <label className="form-label">Imagen de la pregunta</label>
        {uploadedImageUrl ? (
          <div className="uploaded-image-container">
            <div 
              className="d-inline-block position-relative"
              style={{
                width: '200px',
                height: '150px',
                border: '2px solid #dee2e6',
                borderRadius: '0.375rem',
                padding: '4px',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                const img = e.currentTarget.querySelector('img');
                const btn = e.currentTarget.querySelector('button');
                if (img) img.style.filter = 'brightness(0.7)';
                if (btn) btn.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                const img = e.currentTarget.querySelector('img');
                const btn = e.currentTarget.querySelector('button');
                if (img) img.style.filter = 'brightness(1)';
                if (btn) btn.style.opacity = '0';
              }}
            >
              <img 
                src={uploadedImageUrl} 
                alt="Imagen de la pregunta" 
                style={{ 
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'filter 0.2s ease'
                }}
              />
              <button
                type="button"
                className="btn btn-sm btn-outline-dark position-absolute"
                onClick={onImageDelete}
                title="Eliminar imagen"
                style={{
                  top: '8px',
                  right: '8px',
                  padding: '4px 6px',
                  opacity: '0',
                  transition: 'opacity 0.2s ease'
                }}
              >
                <i className="bi bi-trash"></i>
              </button>
            </div>
          </div>
        ) : (
          <div className="border rounded p-4 text-center">
            <i className="bi bi-image fs-1 text-muted d-block mb-2"></i>
            <input
              type="file"
              className="form-control"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={onImageUpload}
              disabled={isUploadingImage}
            />
            <div className="form-text mt-2">
              Formatos permitidos: JPEG, JPG, PNG, WebP. Tamaño máximo: 10MB
            </div>
            {isUploadingImage && (
              <div className="mt-3">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Subiendo imagen...</span>
                </div>
                <span className="ms-2">Subiendo imagen...</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Question Text Section */}
      <div className="mb-4">
        <label className="form-label">Texto de la pregunta</label>
        <small className="text-muted d-block mb-3">
          Configura diferentes variaciones del texto de la pregunta según el tipo de trato (tú/usted) y número (singular/plural).
        </small>
        
        {/* Tab Navigation */}
        <ul className="nav nav-tabs mb-0">
          {tabs.map(tab => (
            <li key={tab.key} className="nav-item">
              <button
                className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
                type="button"
                style={{ color: '#000' }}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>

        {/* Editor for active tab */}
        <div 
          className="border border-top-0"
          style={{ 
            borderRadius: '0 0 0.375rem 0.375rem'
          }}
        >
          <div className="d-flex align-items-center border-bottom bg-light">
            <div className="btn-group h-100" role="group" aria-label="Formato de texto">
              <button
                type="button"
                className="btn btn-outline-secondary h-100 border-0"
                onClick={() => applyFormatting('bold')}
                title="Negrita"
                style={{ borderRadius: 0 }}
              >
                <i className="bi bi-type-bold"></i>
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary h-100 border-0"
                onClick={() => applyFormatting('italic')}
                title="Cursiva"
                style={{ borderRadius: 0 }}
              >
                <i className="bi bi-type-italic"></i>
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary h-100 border-0"
                onClick={() => applyFormatting('underline')}
                title="Subrayado"
                style={{ borderRadius: 0 }}
              >
                <i className="bi bi-type-underline"></i>
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary h-100 border-0"
                onClick={() => {
                  // Try to clean selected text first, if no selection clean all
                  const selection = window.getSelection();
                  if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
                    // Clean format of selected text only
                    document.execCommand('removeFormat', false);
                  } else {
                    // Clean all formatting
                    handleContentChange(stripAllFormatting(variations[activeTab]));
                  }
                }}
                title="Limpiar formato (selección o todo)"
                style={{ borderRadius: 0 }}
              >
                <i className="bi bi-eraser"></i>
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary h-100 border-0"
                onClick={() => applyFormatting('insertUnorderedList')}
                title="Lista desordenada"
                style={{ borderRadius: 0 }}
              >
                <i className="bi bi-list-ul"></i>
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary h-100 border-0"
                onClick={() => applyFormatting('insertOrderedList')}
                title="Lista ordenada"
                style={{ borderRadius: 0 }}
              >
                <i className="bi bi-list-ol"></i>
              </button>
            </div>
          </div>
          
          {/* Content Editor */}
          <ContentEditable
            html={variations[activeTab]}
            disabled={false}
            onChange={(evt) => handleContentChange(evt.target.value)}
            onPaste={handlePaste}
            style={{
              padding: '1rem',
              minHeight: '250px',
              backgroundColor: '#fff',
              border: 'none',
              outline: 'none'
            }}
            className="w-100"
            placeholder={`Escribe el texto de la pregunta para ${tabs.find(t => t.key === activeTab)?.label}...`}
          />
        </div>

        {/* Auto-generate button */}
        {activeTab !== 'original' && (
          <div className="mt-4">
            <button
              type="button"
              className="btn btn-outline-dark"
              onClick={handleAutoGenerate}
              disabled={isGenerating || !variations.original}
              title={!variations.original ? 'Ingresa primero el texto original' : 'Generar texto automáticamente basado en el original'}
            >
              {isGenerating ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Generando...
                </>
              ) : (
                <>
                  <i className="bi bi-magic me-2"></i>
                  Autogenerar texto
                </>
              )}
            </button>
            {!variations.original && (
              <small className="text-warning ms-2">
                <i className="bi bi-exclamation-triangle me-1"></i>
                Ingresa el texto original primero
              </small>
            )}
            {error && (
              <div className="alert alert-danger mt-2" role="alert">
                <i className="bi bi-exclamation-circle me-2"></i>
                {typeof error === 'string' ? error : 'Error al generar el texto'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Editable Toggle - Moved to the end */}
      {showEditableToggle && (
        <div className="mt-4">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="questionImageEditable"
              checked={isEditable}
              onChange={(e) => onEditableChange(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="questionImageEditable" style={{ color: '#000' }}>
              Permitir edición por Arquitectos
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionImageEditor;